import type { JSX, ReactNode, ComponentPropsWithoutRef } from 'react';
import { createContext, useMemo, useState, useCallback, useId } from 'react';
import { isNonEmptyString } from '@wistia/type-guards';
import { styled } from 'styled-components';
import type { TypographicVariant } from '../../private/helpers/getTypographicStyles/getTypographicStyles';

const LARGE_PADDING = 'var(--wui-space-02)';
const SMALL_PADDING = 'var(--wui-space-01)';

const getPaddingForVariant = (variant: TypographicVariant): string => {
  const largePaddingVariants = [
    'hero',
    'heading1',
    'heading2',
    'heading3',
    'body1',
    'label1',
    'body1Mono',
  ];

  const smallPaddingVariants = [
    'heading4',
    'heading5',
    'heading6',
    'body2',
    'body3',
    'body4',
    'label2',
    'label3',
    'label4',
    'body2Mono',
    'body3Mono',
    'body4Mono',
  ];

  if (largePaddingVariants.includes(variant)) {
    return LARGE_PADDING;
  }

  if (smallPaddingVariants.includes(variant)) {
    return SMALL_PADDING;
  }

  return SMALL_PADDING;
};

const StyledEditableTextRoot = styled.div<{ $typographicVariant: TypographicVariant }>`
  display: contents;

  --wui-editable-text-padding: ${({ $typographicVariant }) =>
    getPaddingForVariant($typographicVariant)};
  --wui-editable-text-border-radius: var(--wui-border-radius-01);
`;

export type EditableTextRootProps = Omit<ComponentPropsWithoutRef<'div'>, 'defaultValue'> & {
  /**
   * The children of the editable text root.
   */
  children: ReactNode;
  /**
   * The initial value for the editable text
   */
  defaultValue?: string;
  /**
   * The current value for the editable text
   */
  value?: string;
  /**
   * Callback fired when the value changes
   */
  onValueChange?: (value: string) => void;
  /**
   * Callback fired when editing is committed (e.g., save button clicked, Enter pressed)
   */
  onValueCommit?: (value: string) => void;
  /**
   * Callback fired when editing is cancelled (e.g., cancel button clicked, Escape pressed)
   */
  onValueRevert?: (originalValue: string) => void;
  /**
   * Callback fired when the editing state changes (editing starts or stops)
   */
  onEditingChange?: (isEditing: boolean) => void;
  /**
   * The typographic variant to use for the display component
   */
  typographicVariant?: TypographicVariant;
  /**
   * The action(s) that trigger submit or dismiss in edit mode:
   * - "enter": Trigger submit when the enter key is pressed, or dismiss when the esc key is pressed
   * - "blur": Trigger submit when the editable is blurred, or dismiss when the esc key is pressed
   * - "none": No action will trigger submit or dismiss. You need to use the submit or dismiss buttons
   * - "both": Pressing Enter and blurring the input will trigger submit, or dismiss when the esc key is pressed
   */
  submitMode?: 'blur' | 'both' | 'enter' | 'none';
  /**
   * Whether the editable text is read only. If true, the editable text will not be editable and the trigger will not be shown.
   */
  readOnly?: boolean;
  /**
   * The id of the editable text.
   */
  id?: string;
  /**
   * The label of the editable text.
   */
  label: string;
  /**
   * Placeholder text for the input field and display
   */
  placeholder?: string;
  /**
   * Minimum number of lines to display.
   */
  minLines?: number;
  /**
   * Maximum number of lines to display. If not set, there is no maximum.
   */
  maxLines?: number | undefined;
  /**
   * The element to receive focus when the editable is closed.
   */
  finalFocusEl?: () => HTMLElement | null;
};

export type EditableTextContextValues = {
  isEditing: boolean;
  setIsEditing: (editing: boolean) => void;
  value: string;
  setValue: (value: string) => void;
  originalValue: string;
  onValueCommit: ((value: string) => void) | undefined;
  onValueRevert: ((originalValue: string) => void) | undefined;
  typographicVariant: TypographicVariant;
  submitMode: 'blur' | 'both' | 'enter' | 'none';
  readOnly: boolean;
  id: string;
  label: string;
  placeholder: string;
  minLines: number;
  maxLines: number | undefined;
  finalFocusEl: (() => HTMLElement | null) | undefined;
};

export const EditableTextContext = createContext<EditableTextContextValues | null>(null);

export const EditableTextRoot = ({
  children,
  defaultValue = '',
  value: controlledValue,
  onValueChange,
  onValueCommit,
  onValueRevert,
  onEditingChange,
  typographicVariant = 'body2',
  submitMode = 'both',
  readOnly = false,
  id,
  label,
  placeholder = 'Click to edit this text',
  minLines = 1,
  maxLines,
  finalFocusEl,
  ...props
}: EditableTextRootProps): JSX.Element => {
  const isControlled = controlledValue !== undefined;
  const [internalValue, setInternalValue] = useState(defaultValue);
  const [originalValue, setOriginalValue] = useState(defaultValue);
  const [isEditing, setIsEditing] = useState(false);

  const value = isControlled ? controlledValue : internalValue;

  const generatedId = useId();
  const computedId = isNonEmptyString(id) ? id : `wistia-ui-editable-text-${generatedId}`;

  const handleSetIsEditing = useCallback(
    (editing: boolean) => {
      if (editing && !isEditing) {
        setOriginalValue(value);
      }
      setIsEditing(editing);
      onEditingChange?.(editing);
    },
    [isEditing, value, onEditingChange],
  );

  const setValue = useCallback(
    (newValue: string) => {
      if (!isControlled) {
        setInternalValue(newValue);
      }
      onValueChange?.(newValue);
    },
    [isControlled, onValueChange],
  );

  const context = useMemo<EditableTextContextValues>(() => {
    return {
      isEditing,
      setIsEditing: handleSetIsEditing,
      value,
      setValue,
      originalValue,
      onValueCommit,
      onValueRevert,
      typographicVariant,
      submitMode,
      readOnly,
      id: computedId,
      label,
      placeholder,
      minLines,
      maxLines,
      finalFocusEl,
    };
  }, [
    isEditing,
    handleSetIsEditing,
    value,
    setValue,
    originalValue,
    onValueCommit,
    onValueRevert,
    typographicVariant,
    submitMode,
    readOnly,
    computedId,
    label,
    placeholder,
    minLines,
    maxLines,
    finalFocusEl,
  ]);

  const getState = () => {
    if (readOnly) {
      return 'read-only';
    }
    if (isEditing) {
      return 'editing';
    }
    return 'idle';
  };

  return (
    <StyledEditableTextRoot
      $typographicVariant={typographicVariant}
      data-testid="editable-text-root"
      data-wui-editable-text-root={true}
      data-wui-editable-text-state={getState()}
      {...props}
    >
      <EditableTextContext.Provider value={context}>{children}</EditableTextContext.Provider>
    </StyledEditableTextRoot>
  );
};
